import { Line } from 'react-chartjs-2';
import {
	CategoryScale,
	LineElement,
	Chart,
	LinearScale,
	PointElement,
	Tooltip,
	Title,
	Legend,
} from 'chart.js';
import { eulersMethod } from './model/functions';
import { useState } from 'react';
import '../styles/fonts.css';

Chart.register(
	CategoryScale,
	LineElement,
	LinearScale,
	PointElement,
	Tooltip,
	Title,
	Legend
);

const options = {
	scales: {
		x: {
			ticks: {
				callback: function (value) {
					return value / 10;
				},
			},
		},
	},
	responsive: true,
	maintainAspectRatio: false,
};

const initConsts = {
	larva: 2000,
	pupa: 0,
	sMos: 15000,
	eMos: 0,
	iMos: 0,
	sHum: 2500,
	eHum: 0,
	iHum: 1,
	rHum: 0,
};

const Graph = () => {
	const logo = 'https://static.igem.wiki/teams/5066/global/i2024-logo.webp';

	const [dataPoints, setDataPoints] = useState(2500);
	const [eggLarvaTransitionRate, setEggLarvaTransitionRate] = useState(0.33);
	const [larvaPupaTransitionRate, setLarvaPupaTransitionRate] =
		useState(0.14);
	const [pupaAdultTransitionRate, setPupaAdultTransitionRate] =
		useState(0.346);
	const [larvaDeathRate, setLarvaDeathRate] = useState(0.05);
	const [mosquitoDeathRate, setMosquitoDeathRate] = useState(0.042);
	const [inputValue, setInputValue] = useState([
		{ label: 'Egg', value: initConsts.sHum * 3, name: 'egg' },
		{ label: 'Larva', value: initConsts.larva, name: 'larva' },
		{ label: 'Pupa', value: initConsts.pupa, name: 'pupa' },
		{ label: 'S_mos', value: initConsts.sMos, name: 'sMos' },
		{ label: 'E_mos', value: initConsts.eMos, name: 'eMos' },
		{ label: 'I_mos', value: initConsts.iMos, name: 'iMos' },
		{ label: 'S_hum', value: initConsts.sHum, name: 'sHum' },
		{ label: 'E_hum', value: initConsts.eHum, name: 'eHum' },
		{ label: 'I_hum', value: initConsts.iHum, name: 'iHum' },
		{ label: 'R_hum', value: initConsts.rHum, name: 'rHum' },
	]);

	const initialData = eulersMethod(
		inputValue,
		dataPoints,
		eggLarvaTransitionRate,
		larvaPupaTransitionRate,
		pupaAdultTransitionRate,
		larvaDeathRate,
		mosquitoDeathRate
	);
	const labels = [];
	for (let i = 1; i <= dataPoints; i++) {
		labels.push((i / 10).toString());
	}

	const [larva, setLarva] = useState({
		labels: labels,
		datasets: [
			{
				label: 'Larva',
				data: initialData.map((pnt) => pnt.L),
				fill: false,
				borderColor: 'blue',
			},
		],
	});
	const [infectedHumans, setInfectedHumans] = useState({
		labels: labels,
		datasets: [
			{
				label: 'Infected Humans',
				data: initialData.map((pnt) => pnt.i),
				fill: false,
				borderColor: 'blue',
			},
		],
	});
	const [mosquitos, setMosquitos] = useState({
		labels: labels,
		datasets: [
			{
				label: 'Infected Mosquitos',
				data: initialData.map((pnt) => pnt.Mi),
				fill: false,
				borderColor: 'red',
			},
			{
				label: 'Exposed Mosquitos',
				data: initialData.map((pnt) => pnt.Me),
				fill: false,
				borderColor: 'black',
			},
			{
				label: 'Suceptible Mosquitos',
				data: initialData.map((pnt) => pnt.Ms),
				fill: false,
				borderColor: 'blue',
			},
		],
	});
	const [humans, setHumans] = useState({
		labels: labels,
		datasets: [
			{
				label: 'Infected Humans',
				data: initialData.map((pnt) => pnt.i),
				fill: false,
				borderColor: 'red',
			},
			{
				label: 'Exposed Humans',
				data: initialData.map((pnt) => pnt.e),
				fill: false,
				borderColor: 'black',
			},
			{
				label: 'Suceptible Humans',
				data: initialData.map((pnt) => pnt.s),
				fill: false,
				borderColor: 'blue',
			},
			{
				label: 'Recovered Humans',
				data: initialData.map((pnt) => pnt.r),
				fill: false,
				borderColor: 'green',
			},
		],
	});

	const updateCharts = () => {
		const dataSet = eulersMethod(
			inputValue,
			dataPoints,
			eggLarvaTransitionRate,
			larvaPupaTransitionRate,
			pupaAdultTransitionRate,
			larvaDeathRate,
			mosquitoDeathRate
		);

		const dataLabels = [];
		for (let i = 1; i <= dataPoints; i++) {
			dataLabels.push((i / 10).toString());
		}

		setLarva((prev) => ({
			...prev,
			labels: dataLabels,
			datasets: [
				{
					label: 'Larva',
					data: dataSet.map((pnt) => pnt.L),
					fill: false,
					borderColor: 'blue',
					pointStyle: false,
				},
			],
		}));
		setInfectedHumans({
			labels: dataLabels,
			datasets: [
				{
					label: 'Infected Humans',
					data: dataSet.map((pnt) => pnt.i),
					fill: false,
					borderColor: 'blue',
					pointStyle: false,
				},
			],
		});
		setMosquitos((prev) => ({
			...prev,
			labels: dataLabels,
			datasets: [
				{
					label: 'Infected Mosquitos',
					data: dataSet.map((pnt) => pnt.Mi),
					fill: false,
					borderColor: 'red',
					pointStyle: false,
				},
				{
					label: 'Exposed Mosquitos',
					data: dataSet.map((pnt) => pnt.Me),
					fill: false,
					borderColor: 'black',
					pointStyle: false,
				},
				{
					label: 'Suceptible Mosquitos',
					data: dataSet.map((pnt) => pnt.Ms),
					fill: false,
					borderColor: 'blue',
					pointStyle: false,
				},
			],
		}));
		setHumans((prev) => ({
			...prev,
			labels: dataLabels,
			datasets: [
				{
					label: 'Infected Humans',
					data: dataSet.map((pnt) => pnt.i),
					fill: false,
					borderColor: 'red',
					pointStyle: false,
				},
				{
					label: 'Exposed Humans',
					data: dataSet.map((pnt) => pnt.e),
					fill: false,
					borderColor: 'black',
					pointStyle: false,
				},
				{
					label: 'Suceptible Humans',
					data: dataSet.map((pnt) => pnt.s),
					fill: false,
					borderColor: 'blue',
					pointStyle: false,
				},
				{
					label: 'Recovered Humans',
					data: dataSet.map((pnt) => pnt.r),
					fill: false,
					borderColor: 'green',
					pointStyle: false,
				},
			],
		}));
	};

	return (
		<div className='pt-12 flex flex-col xl:flex-row'>
			<div className='flex flex-col flex-grow'>
				<div className='flex flex-col lg:flex-row max-lg:items-center h-1/2'>
					<div className='w-1/2'>
						<Line
							data={larva}
							options={{
								...options,
								plugins: {
									legend: {
										display: false,
										labels: { usePointStyle: false },
									},
									title: {
										display: true,
										text: 'Larva',
										padding: {
											top: 0,
											left: 0,
											right: 0,
											bottom: 4,
										},
									},
								},
							}}
						/>
					</div>
					<div className='w-1/2'>
						<Line
							data={infectedHumans}
							options={{
								...options,
								plugins: {
									legend: {
										display: false,
										labels: { usePointStyle: false },
									},
									title: {
										display: true,
										text: 'Invected Humans',
										padding: {
											top: 0,
											left: 0,
											right: 0,
											bottom: 4,
										},
									},
								},
							}}
						/>
					</div>
				</div>

				<div className='flex flex-col lg:flex-row max-lg:items-center h-1/2'>
					<div className='w-1/2'>
						<Line
							data={mosquitos}
							options={{
								...options,
								plugins: {
									legend: {
										display: false,
										labels: { usePointStyle: false },
									},
									title: {
										display: true,
										text: 'Mosquitos (S, E, I)',
										padding: {
											top: 0,
											left: 0,
											right: 0,
											bottom: 4,
										},
									},
								},
							}}
						/>
					</div>
					<div className='w-1/2'>
						<Line
							data={humans}
							options={{
								...options,
								plugins: {
									legend: {
										display: false,
										labels: { usePointStyle: false },
									},
									title: {
										display: true,
										text: 'Humans (S, E, I, R)',
										padding: {
											top: 0,
											left: 0,
											right: 0,
											bottom: 4,
										},
									},
								},
							}}
						/>
					</div>
				</div>
			</div>
			<div className='flex flex-col'>
				<div className='flex flex-col justify-evenly text-nowrap'>
					{/* data points */}
					<img src={logo} alt='logo' className=' w-24 h-auto' />
					<div className='flex'>
						<p>Data Points:</p>
						<input
							className='max-w-20'
							value={dataPoints}
							onChange={(e) => {
								const re = /^[0-9\b]+$/;
								if (
									e.target.value !== '' ||
									re.test(e.target.value)
								) {
									setDataPoints(
										parseInt(e.target.value) >= 10950
											? 10950
											: parseInt(e.target.value) <= 0
											? 0
											: parseInt(e.target.value)
									);
								} else {
									setDataPoints(0);
								}
							}}
						/>
					</div>
					<input
						type='range'
						min={0}
						max={10950}
						value={dataPoints}
						onChange={(e) =>
							setDataPoints(parseInt(e.target.value))
						}
						step='10'
					/>

					{/* eggLarvaTransitionRate */}
					{`Egg Larva Transition Rate:${eggLarvaTransitionRate}`}
					<input
						type='range'
						min={0}
						max={2}
						value={eggLarvaTransitionRate}
						step='0.01'
						onChange={(e) =>
							setEggLarvaTransitionRate(
								parseFloat(e.target.value)
							)
						}
					/>

					{/* larvaPupaTransitionRate */}
					{`Larva Pupa Transition Rate:${larvaPupaTransitionRate}`}
					<input
						type='range'
						min={0}
						max={2}
						value={larvaPupaTransitionRate}
						step='0.01'
						onChange={(e) =>
							setLarvaPupaTransitionRate(
								parseFloat(e.target.value)
							)
						}
					/>

					{/* pupaAdultTransitionRate */}
					{`Pupa Adult Transition Rate:${pupaAdultTransitionRate}`}
					<input
						type='range'
						min={0}
						max={2}
						value={pupaAdultTransitionRate}
						step='0.001'
						onChange={(e) =>
							setPupaAdultTransitionRate(
								parseFloat(e.target.value)
							)
						}
					/>

					{/* larvaDeathRate */}
					{`Larva Death Rate:${larvaDeathRate}`}
					<input
						type='range'
						min={0}
						max={5}
						value={larvaDeathRate}
						step='0.01'
						onChange={(e) =>
							setLarvaDeathRate(parseFloat(e.target.value))
						}
					/>

					{/* mosquitoDeathRate */}
					{`Mosquito Death Rate:${mosquitoDeathRate}`}
					<input
						type='range'
						min={0}
						max={5}
						value={mosquitoDeathRate}
						step='0.001'
						onChange={(e) =>
							setMosquitoDeathRate(parseFloat(e.target.value))
						}
					/>
				</div>
				<div className='flex flex-col text-nowrap justify-between'>
					{inputValue.map(({ label, value }, i) => (
						<label key={i}>
							{`${label}: `}
							<input
								className='max-w-20'
								value={value}
								onChange={(e) => {
									const re = /^[0-9\b]+$/;
									if (
										e.target.value === '' ||
										re.test(e.target.value)
									) {
										setInputValue(
											inputValue.map((el) =>
												el.label === label
													? {
															...el,
															value:
																e.target
																	.value ===
																''
																	? 0
																	: parseInt(
																			e
																				.target
																				.value
																	  ),
													  }
													: el
											)
										);
									}
								}}
							/>
						</label>
					))}
					<div>
						<button
							className='bg-cyan-400 p-2 rounded my-2'
							onClick={updateCharts}
						>
							Run Model!
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Graph;
